'use client';

import React, { useState } from 'react';
import { Send } from 'lucide-react';
import type { DocumentLine } from '@paeffgen/react/crm-documents/data-access-graphql';
import { INQUIRY_TYPES } from '@paeffgen/react/crm-documents/util';
import { Modal } from './modal';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface InquiryModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Context: either a specific article line, a document, or general */
  context?: {
    article?: Pick<DocumentLine, 'articleNumber'>;
    documentNumber?: string;
  };
  /**
   * Called when the user submits. The caller is responsible for the actual
   * mutation / API call. If no onSubmit is provided, the modal shows a
   * simulated success alert (prototype behaviour).
   *
   * NOTE: A backend mutation for inquiries does not exist yet.
   * When one is added, the caller should wire it up via this callback.
   */
  onSubmit?: (data: {
    subject: string;
    type: string;
    message: string;
  }) => void | Promise<void>;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function InquiryModal({
  isOpen,
  onClose,
  context,
  onSubmit,
}: InquiryModalProps) {
  const [type, setType] = useState('PRICE');
  const [message, setMessage] = useState('');

  const subject = context?.article
    ? `Anfrage zu Artikel ${context.article.articleNumber}`
    : context?.documentNumber
      ? `Anfrage zu Beleg ${context.documentNumber}`
      : 'Allgemeine Anfrage';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (onSubmit) {
      await onSubmit({ subject, type, message });
    }

    onClose();
    setMessage('');
    setType('PRICE');
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Anfrage an Thomas Wegener"
      className="max-w-xl"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Betreff
          </label>
          <input
            type="text"
            value={subject}
            readOnly
            className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Art der Anfrage
          </label>
          <select
            value={type}
            onChange={(e) => setType(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
          >
            {INQUIRY_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Nachricht
          </label>
          <textarea
            rows={4}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Ihre Nachricht an Herrn Wegener..."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
            required
          />
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Abbrechen
          </button>
          <button
            type="submit"
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-hover shadow-sm flex items-center gap-2"
          >
            <Send size={16} />
            Anfrage senden
          </button>
        </div>
      </form>
    </Modal>
  );
}
